// The one radiogroup behaviour G2's tile groups share (#22 LevelGroup, #24 RatingScale, #25 RecoTiles): a value that
// is controlled or kept here, one tab stop (the checked tile, else the first), arrows that move and select with wrap,
// Home/End, Space/Enter to choose, and DC-24's blocked form: a blocked tile (or the whole group) keeps its tab stop,
// is aria-disabled, points aria-describedby at its inline reason, and choosing it does nothing.

import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { useRoving } from "../lib/useRoving";

export interface ChoiceOptions<V> {
  values: readonly V[];
  value?: V | null | undefined;
  defaultValue?: V | null | undefined;
  onChange?: ((value: V) => void) | undefined;
  /** A tile that may not be chosen (blocked, or the whole group blocked or read-only). */
  isBlocked?: ((value: V) => boolean) | undefined;
}

export interface ChoiceItem {
  role: "radio";
  "aria-checked": boolean;
  tabIndex: 0 | -1;
  ref: (element: HTMLElement | null) => void;
  onFocus: () => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useChoice<V>({ values, value, defaultValue, onChange, isBlocked }: ChoiceOptions<V>) {
  const [own, setOwn] = useState<V | null>(defaultValue ?? null);
  const chosen = value !== undefined ? value : own;
  const index = chosen === null ? -1 : values.indexOf(chosen);
  const choose = (next: V) => {
    if (isBlocked?.(next)) return;
    if (value === undefined) setOwn(next);
    onChange?.(next);
  };
  const roving = useRoving({
    count: values.length,
    active: index,
    orientation: "both",
    selectOnFocus: true,
    onSelect: (at) => {
      const next = values[at];
      if (next !== undefined) choose(next);
    },
  });
  const item = (at: number): ChoiceItem => {
    const v = values[at] as V;
    const own = roving.item(at);
    return {
      role: "radio",
      "aria-checked": at === index,
      tabIndex: own.tabIndex,
      ref: own.ref,
      onFocus: own.onFocus,
      onClick: () => {
        roving.focus(at);
        choose(v);
      },
      onKeyDown: (event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          choose(v);
        }
      },
    };
  };
  return { chosen, index, item, container: roving.container };
}
